import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { usePreferences } from '../../hooks/usePreferences';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import AddDocumentTypeModal from './AddDocumentTypeModal';
import SelectTemplateModal from './SelectTemplateModal';
import CreateDocumentTypeModal from './CreateDocumentTypeModal';
import AddVisitTypeModal from './AddVisitTypeModal';
import SelectVisitTypeModal from './SelectVisitTypeModal';
import CreateVisitTypeModal from './CreateVisitTypeModal';
import { toSlug } from '../../utils/stringUtils';
import { getTemplateName } from '../../services/supabaseClient';

const Breadcrumb = ({ items }) => (
  <nav className="flex" aria-label="Breadcrumb">
    <ol className="flex items-center space-x-2">
      {items.map((item, index) => (
        <li key={item.href || item.label} className="flex items-center">
          {index > 0 && (
            <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
          )}
          {item.href ? (
            <Link 
              to={item.href}
              className="text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              {item.label}
            </Link>
          ) : (
            <span className="text-sm font-medium text-gray-900">{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

const ReviewPreferences = () => {
  const { documentType, visitType, templateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const {
    loading,
    documentTypes,
    visitTypes,
    visitDetails,
    fetchDocumentTypes,
    fetchVisitTypes,
    fetchVisitDetails,
    updateReportStyle,
    fetchReportStyle,
    fetchTemplate,
    fetchDocumentTemplates,
    createDocumentType,
    fetchCategories,
    createVisitType,
    createVisitDetail,
    updateVisitDetail,
    updateDocumentType,
    fetchDocumentType,
    fetchVisitType,
    fetchVisitDetail,
    updateVisitType,
    setVisitDetails
  } = usePreferences();

  const [templateName, setTemplateName] = useState('');
  const [expandedDetailId, setExpandedDetailId] = useState(null);
  const [reportStyle, setReportStyle] = useState({
    generalInstructions: '',
    defaultConcision: 50,
    preferredTerminology: '',
  });

  const [fullReportStyle, setFullReportStyle] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showAddVisitTypeModal, setShowAddVisitTypeModal] = useState(false);
  const [showVisitTemplateModal, setShowVisitTemplateModal] = useState(false);
  const [showCreateVisitTypeModal, setShowCreateVisitTypeModal] = useState(false);
  const [documentFormData, setDocumentFormData] = useState({
    name: '',
    category: '',
    description: '',
  });

  const [visitFormData, setVisitFormData] = useState({
    name: '',
    description: '',
    details: {}
  });

  const scrollRef = useRef(null);

  const [unsavedVisitDetails, setUnsavedVisitDetails] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load initial data
  useEffect(() => {
    if (templateId) {
      fetchDocumentTypes(templateId);
      const loadInitialData = async () => {
        try {
          const response = await fetchReportStyle(templateId);
          const { source_template_id, report_style } = response;
          
          // Store full report style data
          setFullReportStyle(report_style);
          
          const formState = {
            generalInstructions: report_style?.general?.instructions || '',
            defaultConcision: Number(report_style?.general?.concision?.value || 50),
            preferredTerminology: Array.isArray(report_style?.general?.terminology?.preferred) 
              ? report_style.general.terminology.preferred.join(', ') 
              : ''
          };
          
          // Set form state
          setReportStyle(formState);
        } catch (error) {
          console.error('Error loading report style:', error);
        }
      };
      loadInitialData();
    }
  }, [templateId]);

  // Load visit types when document type is selected
  useEffect(() => {
    if (documentType && templateId) {
      fetchVisitTypes(documentType, templateId);
    }
  }, [documentType, templateId]);

  // Load visit details when visit type is selected
  useEffect(() => {
    if (visitType) {
      fetchVisitDetails(visitType, templateId);
    }
  }, [visitType]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategories();
  }, []);

  // Update form data when document type changes
  useEffect(() => {
    if (documentType && templateId) {
      const loadDocumentType = async () => {
        try {
          const docTypeData = await fetchDocumentType(templateId, documentType);
          setDocumentFormData({
            name: docTypeData.name,
            category: docTypeData.category,
            description: docTypeData.description,
          });
        } catch (error) {
          console.error('Error loading document type:', error);
        }
      };
      loadDocumentType();
    }
  }, [documentType, templateId]);

  useEffect(() => {
    if (visitType && templateId) {
      const loadVisitType = async () => {
        try {
          const visitTypeData = await fetchVisitType(templateId, visitType);
          setVisitFormData({
            name: visitTypeData.data.name,
            description: visitTypeData.data.description,
            details: visitTypeData.data.details || {}
          });
        } catch (error) {
          console.error('Error loading visit type:', error);
        }
      };
      loadVisitType();
    }
  }, [visitType, templateId]);

  // Add effect to fetch template name
  useEffect(() => {
    const loadTemplateName = async () => {
      if (templateId) {
        const name = await getTemplateName(templateId);
        setTemplateName(name || 'Review Preferences');
      }
    };
    loadTemplateName();
  }, [templateId]);

  const handleStyleChange = (field, value) => {
    setReportStyle(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStyleBlur = async (field) => {
    try {
      const apiStyle = {
        general: {
          instructions: reportStyle.generalInstructions,
          concision: {
            level: reportStyle.defaultConcision < 33 ? 'detailed' : 
                   reportStyle.defaultConcision < 66 ? 'moderate' : 'concise',
            value: Number(reportStyle.defaultConcision)
          },
          terminology: {
            preferred: reportStyle.preferredTerminology.split(',').map(t => t.trim()).filter(Boolean),
            avoided: []
          },
          concisionExamples: fullReportStyle?.general?.concisionExamples || {}
        }
      };
      await updateReportStyle(templateId, apiStyle);
    } catch (error) {
      console.error('Error updating report style:', error);
      toast.error('Failed to save changes');
    }
  };

  const handleVisitDetailChange = (detailId, changes) => {
    setUnsavedVisitDetails(prev => ({
      ...prev,
      [detailId]: {
        ...(prev[detailId] || {}),
        ...changes
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleSaveChanges = async () => {
    try {
      if (visitType) {
        // Save visit type metadata
        const visitData = {
          name: visitFormData.name,
          description: visitFormData.description,
          details: visitFormData.details || {}
        };
        await updateVisitType(templateId, visitType, visitData);

        // Save all visit details changes
        for (const [detailId, changes] of Object.entries(unsavedVisitDetails)) {
          const currentDetail = visitDetails.find(d => d.detail_id === detailId);
          const updatedDetail = {
            ...currentDetail,
            ...changes,
            required: currentDetail?.required || [],
            promptTemplate: currentDetail?.promptTemplate || ''
          };
          await updateVisitDetail(templateId, visitType, detailId, updatedDetail);
        }

        // Clear unsaved changes
        setUnsavedVisitDetails({});
        setHasUnsavedChanges(false);
        
        // Refresh the data
        await fetchVisitDetails(visitType, templateId);
        toast.success('Changes saved successfully');
      } else if (documentType) {
        await handleUpdateDocumentType();
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes');
    }
  };

  // Add effect to track re-renders
  useEffect(() => {
    console.log('Component re-rendered, scroll position:', window.scrollY);
  });

  const getBreadcrumbItems = () => {
    const items = [{ 
      label: templateName || 'Review Preferences', 
      href: templateId ? `/templates/${templateId}` : '/preferences'
    }];
    
    if (documentType) {
      const docTypeObj = documentTypes.find(d => d.document_type_id === documentType);
      items.push({ 
        label: docTypeObj?.name || documentType,
        href: documentType && !visitType ? null : `/templates/${templateId}/document-types/${documentType}`
      });
    }
    
    if (visitType) {
      const visitTypeObj = visitTypes.find(v => v.visit_type_id === visitType);
      items.push({ 
        label: visitTypeObj?.name || visitType,
        href: null
      });
    }
    
    return items;
  };

  const handleAddDocumentType = () => {
    setShowAddModal(true);
  };

  const handleSelectTemplate = () => {
    setShowAddModal(false);
    setShowTemplateModal(true);
  };

  const handleCreateNew = () => {
    setShowAddModal(false);
    setShowCreateModal(true);
  };

  const handleTemplateSelected = async (template) => {
    try {
      // Generate a new unique ID for this document type
      const newDocTypeId = `${template.document_type_id}-${Date.now()}`;
      
      // Create new document type from template, passing the example_doc_type_id as a query parameter
      const newDocType = await createDocumentType(
        templateId,
        newDocTypeId,
        {
          name: template.name,
          category: template.category,
          description: template.description,
        },
        template.document_type_id  // Pass as example_doc_type_id query parameter
      );

      // Close modal and refresh list
      setShowTemplateModal(false);
      await fetchDocumentTypes(templateId);

      // Navigate to the new document type
      navigate(`/templates/${templateId}/document-types/${newDocTypeId}`, {
        state: { ...location.state }
      });
    } catch (error) {
      console.error('Error creating from template:', error);
      toast.error('Failed to create document type from template');
    }
  };

  const handleCreateSubmit = async (templateId, docType, docData) => {
    try {
      const newDocType = await createDocumentType(templateId, docType, docData);
      setShowCreateModal(false);
      await fetchDocumentTypes(templateId);
      
      // Update navigation to include templateId in the path
      navigate(`/templates/${templateId}/document-types/${docType}`, {
        state: { ...location.state }  // Preserve any existing state
      });
    } catch (error) {
      console.error('Error creating document type:', error);
    }
  };

  const handleAddVisitType = () => {
    setShowAddVisitTypeModal(true);
  };

  const handleSelectVisitTemplate = () => {
    setShowAddVisitTypeModal(false);
    setShowVisitTemplateModal(true);
  };

  const handleCreateNewVisitType = () => {
    setShowAddVisitTypeModal(false);
    setShowCreateVisitTypeModal(true);
  };

  const handleVisitTemplateSelected = async (template) => {
    try {
      // Generate a new unique ID for this visit type
      const newVisitTypeId = `${template.visit_type_id}-${Date.now()}`;

      // Create new visit type from template
      const newVisitType = await createVisitType(
        templateId,
        newVisitTypeId,
        {
          name: template.name,
          description: template.description,
          details: {}  // Details will be copied from example
        },
        documentType,  // Current document type ID
        template.visit_type_id  // Pass as example_visit_type_id
      );

      // Close modal and refresh list
      setShowVisitTemplateModal(false);
      await fetchVisitTypes(documentType, templateId);

      // Navigate to the new visit type
      navigate(`/templates/${templateId}/document-types/${documentType}/visit-types/${newVisitTypeId}`, {
        state: { ...location.state }
      });
    } catch (error) {
      console.error('Error creating from template:', error);
      toast.error('Failed to create visit type from template');
    }
  };

  const handleCreateVisitTypeSubmit = async (formData) => {
    try {
      const visitTypeId = toSlug(formData.name);
      const visitData = {
        name: formData.name,
        description: formData.description,
        details: {}  // Start with empty details
      };

      await createVisitType(templateId, visitTypeId, visitData, documentType);
      setShowCreateVisitTypeModal(false);
      await fetchVisitTypes(documentType, templateId);
      
      // Navigate to the new visit type
      navigate(`/templates/${templateId}/document-types/${documentType}/visit-types/${visitTypeId}`, {
        state: { ...location.state }
      });
    } catch (error) {
      console.error('Error creating visit type:', error);
      toast.error('Failed to create visit type');
    }
  };

  const handleAddVisitDetail = async () => {
    try {
      // Create a new detail with a default name
      const detailName = `new-detail-${visitDetails.length + 1}`;
      
      await createVisitDetail(templateId, visitType, detailName);
      await fetchVisitDetails(visitType, templateId);
      toast.success('Visit detail added successfully');
    } catch (error) {
      console.error('Error creating visit detail:', error);
      toast.error('Failed to add visit detail');
    }
  };

  const handleUpdateDocumentType = async () => {
    try {
      const currentDocType = documentTypes.find(d => d.document_type_id === documentType);
      const docData = {
        name: documentFormData.name,
        category: documentFormData.category,
        description: documentFormData.description,
        visitTypes: currentDocType?.visitTypes || [],
        required: currentDocType?.required || ['date', 'provider', 'facility']
      };

      await updateDocumentType(templateId, documentType, docData);
      await fetchDocumentTypes(templateId);
      toast.success('Document type updated successfully');
    } catch (error) {
      console.error('Error updating document type:', error);
      toast.error('Failed to update document type');
    }
  };

  const handleUpdateVisitType = async () => {
    try {
      const visitData = {
        name: visitFormData.name,
        description: visitFormData.description,
        details: visitFormData.details || {}
      };

      await updateVisitType(templateId, visitType, visitData);
      toast.success('Visit type updated successfully');
    } catch (error) {
      console.error('Error updating visit type:', error);
      toast.error('Failed to update visit type');
    }
  };

  const getConcisionExample = (value) => {
    const examples = fullReportStyle?.general?.concisionExamples;
    
    if (!examples) return '';
    
    // Get all thresholds and sort them numerically
    const thresholds = Object.keys(examples)
      .map(Number)
      .sort((a, b) => a - b);
    
    // Find the first threshold that's greater than our value
    const threshold = thresholds.find(t => value <= t) || thresholds[thresholds.length - 1];
    
    return examples[threshold] || '';
  };

  const renderMainPreferences = () => {
    return (
      <div className="space-y-8">
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-6">Report Style</h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                General Instructions
              </label>
              <textarea
                value={reportStyle.generalInstructions}
                onChange={(e) => handleStyleChange('generalInstructions', e.target.value)}
                onBlur={() => handleStyleBlur('generalInstructions')}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter any general instructions for report generation..."
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Concision
              </label>
              <div className="grid grid-cols-2 gap-8">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Detailed</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={reportStyle.defaultConcision}
                    onChange={(e) => handleStyleChange('defaultConcision', e.target.value)}
                    onMouseUp={() => handleStyleBlur('defaultConcision')}
                    onTouchEnd={() => handleStyleBlur('defaultConcision')}
                    className="w-96 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-sm text-gray-600">Concise</span>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Example Detail</h4>
                  <p className="text-sm text-gray-600">
                    {getConcisionExample(reportStyle.defaultConcision)}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Terminology
              </label>
              <textarea
                value={reportStyle.preferredTerminology}
                onChange={(e) => handleStyleChange('preferredTerminology', e.target.value)}
                onBlur={() => handleStyleBlur('preferredTerminology')}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter preferred medical terms and abbreviations..."
                rows={4}
              />
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Document Types</h2>
          <div className="space-y-2">
            {documentTypes.map((docType) => (
              <div
                key={docType.document_type_id}
                onClick={() => navigate(
                  `/templates/${templateId}/document-types/${docType.document_type_id}`, 
                  { 
                    state: { 
                      ...location.state,
                      documentName: docType.name
                    } 
                  }
                )}
                className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
              >
                <span className="text-sm font-medium text-gray-900">{docType.name}</span>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            ))}
            <button
              onClick={handleAddDocumentType}
              className="w-full p-4 text-center border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
            >
              + Add Document Type
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDocumentTypePreferences = () => {
    const currentDocType = documentTypes.find(d => d.document_type_id === documentType);
    
    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Document Type Name
          </label>
          <input
            type="text"
            value={documentFormData.name}
            onChange={(e) => setDocumentFormData({ ...documentFormData, name: e.target.value })}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <select
            value={documentFormData.category}
            onChange={(e) => setDocumentFormData({ ...documentFormData, category: e.target.value })}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">Select Category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={documentFormData.description}
            onChange={(e) => setDocumentFormData({ ...documentFormData, description: e.target.value })}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            rows={4}
          />
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleUpdateDocumentType}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
          >
            Save Changes
          </button>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Visit Types</h3>
            {visitTypes.length === 0 && (
              <span className="text-sm text-gray-500">
                No visit types defined for this document type
              </span>
            )}
          </div>
          <div className="space-y-2">
            {visitTypes.map((visitType) => (
              <div
                key={visitType.visit_type_id}
                onClick={() => navigate(
                  `/templates/${templateId}/document-types/${documentType}/visit-types/${visitType.visit_type_id}`,
                  { state: { ...location.state } }
                )}
                className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
              >
                <span className="text-sm font-medium text-gray-900">{visitType.name}</span>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            ))}
            <button
              onClick={handleAddVisitType}
              className="w-full p-4 text-center border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
            >
              + Add Visit Type
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderVisitTypePreferences = () => {
    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Visit Type Name
          </label>
          <input
            type="text"
            value={visitFormData.name}
            onChange={(e) => {
              setVisitFormData({ ...visitFormData, name: e.target.value });
              setHasUnsavedChanges(true);
            }}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={visitFormData.description}
            onChange={(e) => {
              setVisitFormData({ ...visitFormData, description: e.target.value });
              setHasUnsavedChanges(true);
            }}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            rows={4}
          />
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Visit Details</h3>
          <div className="space-y-4">
            {visitDetails.map((detail) => (
              <div 
                key={detail.detail_id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden"
              >
                <div 
                  onClick={() => setExpandedDetailId(expandedDetailId === detail.detail_id ? null : detail.detail_id)}
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{detail.name}</h4>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{detail.description}</p>
                  </div>
                  <ChevronRight 
                    className={`h-5 w-5 text-gray-400 transform transition-transform duration-200 ${
                      expandedDetailId === detail.detail_id ? 'rotate-90' : ''
                    }`} 
                  />
                </div>

                {expandedDetailId === detail.detail_id && (
                  <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        defaultValue={detail.name}
                        onChange={(e) => handleVisitDetailChange(detail.detail_id, {
                          name: e.target.value
                        })}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        defaultValue={detail.description}
                        onChange={(e) => handleVisitDetailChange(detail.detail_id, {
                          description: e.target.value
                        })}
                        rows={2}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Instructions
                      </label>
                      <textarea
                        defaultValue={detail.instructions}
                        onChange={(e) => handleVisitDetailChange(detail.detail_id, {
                          instructions: e.target.value
                        })}
                        rows={3}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Example
                      </label>
                      <textarea
                        defaultValue={detail.example}
                        onChange={(e) => handleVisitDetailChange(detail.detail_id, {
                          example: e.target.value
                        })}
                        rows={2}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
            <button
              onClick={handleAddVisitDetail}
              className="w-full p-4 text-center border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
            >
              + Add Visit Detail
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto px-4 py-6">
        <div className="mb-6 flex justify-between items-center">
          <Breadcrumb items={getBreadcrumbItems()} />
          {(visitType || documentType) && (
            <button
              onClick={handleSaveChanges}
              disabled={!hasUnsavedChanges}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                hasUnsavedChanges 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              Save Changes
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            {visitType ? renderVisitTypePreferences() :
             documentType ? renderDocumentTypePreferences() :
             renderMainPreferences()}
          </div>
        </div>
      </div>

      <AddDocumentTypeModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSelectTemplate={handleSelectTemplate}
        onCreateNew={handleCreateNew}
      />

      <SelectTemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSelect={handleTemplateSelected}
        templateId={templateId}
      />

      <CreateDocumentTypeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSubmit}
        categories={categories}
        templateId={templateId}
      />

      <AddVisitTypeModal
        isOpen={showAddVisitTypeModal}
        onClose={() => setShowAddVisitTypeModal(false)}
        onSelectTemplate={handleSelectVisitTemplate}
        onCreateNew={handleCreateNewVisitType}
      />

      <SelectVisitTypeModal
        isOpen={showVisitTemplateModal}
        onClose={() => setShowVisitTemplateModal(false)}
        onSelect={handleVisitTemplateSelected}
        documentTypeId={documentType}
        templateId={templateId}
      />

      <CreateVisitTypeModal
        isOpen={showCreateVisitTypeModal}
        onClose={() => setShowCreateVisitTypeModal(false)}
        onSubmit={handleCreateVisitTypeSubmit}
      />
    </>
  );
};

export default ReviewPreferences; 